import { CellStyle } from 'ag-grid-community'
import { colorPalette } from '../../../style/colorPalette'
import { WorkloadUnit } from './commonColumnDef'
import { extractYearMonth } from './periodUtils'

export const ALERT_COLORS = {
  caution: colorPalette.pink[1],
  warning: colorPalette.pink[2],
}

export interface AlertThreshold {
  resourcePlanCautionThreshold: number
  resourcePlanWarningThreshold: number
  estimatedOverCautionThreshold: number
  estimatedOverWarningThreshold: number
  estimatedUnderCautionThreshold: number
  estimatedUnderWarningThreshold: number
  actualOverCautionThreshold: number
  actualOverWarningThreshold: number
  actualUnderCautionThreshold: number
  actualUnderWarningThreshold: number
}

export interface PeriodWorkingDay {
  businessDays: number
  workHours: number
}

export const calculateWeeklyMonthAlertLimit = (
  periodWorkingDays: number,
  monthWorkingDays: number
): number => {
  if (monthWorkingDays === 0) return 0
  return (1.0 * periodWorkingDays) / monthWorkingDays
}

export const calculateAlertUpperLimit = (
  workloadUnit: WorkloadUnit,
  periodData: PeriodWorkingDay
): number => {
  switch (workloadUnit) {
    case 'MONTH':
      return 1.0
    case 'DAY':
      return periodData.businessDays
    case 'HOUR':
      return periodData.workHours
    default:
      return Infinity
  }
}

const scaleForCompare = (n: number): number => Math.round(n * 10000)

export const applyCellAlertStyle = (
  value: number,
  alertUpperLimit: number,
  baseStyle: CellStyle
): CellStyle => {
  if (scaleForCompare(value) > scaleForCompare(alertUpperLimit)) {
    return { ...baseStyle, backgroundColor: ALERT_COLORS.caution }
  }
  return baseStyle
}

const isOverThresholdPercent = (
  value: number,
  base: number,
  thresholdPercent: number
): boolean =>
  scaleForCompare(value * 100) > scaleForCompare(base * thresholdPercent)

const isUnderThresholdPercent = (
  value: number,
  base: number,
  thresholdPercent: number
): boolean =>
  scaleForCompare(value * 100) < scaleForCompare(base * thresholdPercent)

export const applyCellAlertStyleForResourcePlan = (
  value: number,
  alertUpperLimit: number,
  baseStyle: CellStyle,
  alertThreshold: AlertThreshold
): CellStyle => {
  if (
    isOverThresholdPercent(
      value,
      alertUpperLimit,
      alertThreshold.resourcePlanWarningThreshold
    )
  ) {
    return { ...baseStyle, backgroundColor: ALERT_COLORS.warning }
  }
  if (
    isOverThresholdPercent(
      value,
      alertUpperLimit,
      alertThreshold.resourcePlanCautionThreshold
    )
  ) {
    return { ...baseStyle, backgroundColor: ALERT_COLORS.caution }
  }
  return baseStyle
}

export const applyCellAlertStyleForEstimated = (
  value: number,
  resourcePlanValue: number,
  baseStyle: CellStyle,
  alertThreshold: AlertThreshold
): CellStyle => {
  if (value <= 0 || resourcePlanValue <= 0) return baseStyle

  if (
    isOverThresholdPercent(
      value,
      resourcePlanValue,
      alertThreshold.estimatedOverWarningThreshold
    ) ||
    isUnderThresholdPercent(
      value,
      resourcePlanValue,
      alertThreshold.estimatedUnderWarningThreshold
    )
  ) {
    return { ...baseStyle, backgroundColor: ALERT_COLORS.warning }
  }
  if (
    isOverThresholdPercent(
      value,
      resourcePlanValue,
      alertThreshold.estimatedOverCautionThreshold
    ) ||
    isUnderThresholdPercent(
      value,
      resourcePlanValue,
      alertThreshold.estimatedUnderCautionThreshold
    )
  ) {
    return { ...baseStyle, backgroundColor: ALERT_COLORS.caution }
  }
  return baseStyle
}

export const applyCellAlertStyleForActual = (
  value: number,
  estimatedValue: number,
  baseStyle: CellStyle,
  alertThreshold: AlertThreshold
): CellStyle => {
  if (value <= 0 || estimatedValue <= 0) return baseStyle

  if (
    isOverThresholdPercent(
      value,
      estimatedValue,
      alertThreshold.actualOverWarningThreshold
    ) ||
    isUnderThresholdPercent(
      value,
      estimatedValue,
      alertThreshold.actualUnderWarningThreshold
    )
  ) {
    return { ...baseStyle, backgroundColor: ALERT_COLORS.warning }
  }
  if (
    isOverThresholdPercent(
      value,
      estimatedValue,
      alertThreshold.actualOverCautionThreshold
    ) ||
    isUnderThresholdPercent(
      value,
      estimatedValue,
      alertThreshold.actualUnderCautionThreshold
    )
  ) {
    return { ...baseStyle, backgroundColor: ALERT_COLORS.caution }
  }
  return baseStyle
}

export const calculateAlertLimitFromCalendar = (
  workingDayCalendars: { [key: string]: PeriodWorkingDay },
  periodKey: string,
  workloadUnit: WorkloadUnit,
  timeGrain?: 'MONTH' | 'WEEK'
): number => {
  const periodData = workingDayCalendars[periodKey]
  if (!periodData) return Infinity

  if (workloadUnit === 'MONTH' && timeGrain === 'WEEK') {
    const yearMonth = extractYearMonth(periodKey)
    if (!yearMonth) return 1.0

    const monthData = workingDayCalendars[yearMonth]
    if (!monthData || monthData.businessDays <= 0) return 1.0

    return calculateWeeklyMonthAlertLimit(
      periodData.businessDays,
      monthData.businessDays
    )
  }

  return calculateAlertUpperLimit(workloadUnit, periodData)
}
